import React from 'react';
import { useNavigate } from 'react-router-dom';
import { APP_COLORS as C, APP_FONT as F } from './tokens';
import { useDashboardData } from './useDashboardData';
import { useVelaQueue } from './useVelaQueue';
import { DEFAULT_SUGGESTIONS } from './constants';
import GreetingBar from './components/GreetingBar';
import VelaPreparedHero from './components/VelaPreparedHero';
import ServicePills from './components/ServicePills';
import CreateActions from './components/CreateActions';
import BusinessGlance from './components/BusinessGlance';
import VelaReviewQueue from './components/VelaReviewQueue';
import CashFlowCard from './components/CashFlowCard';
import RecentTransactions from './components/RecentTransactions';
import Suggestions from './components/Suggestions';
import AddWidgetsCard from './components/AddWidgetsCard';
import VelaChatButton from './components/VelaChatButton';

/**
 * Dashboard orchestrator. Thin: wires hooks + layout.
 * All real UI lives in ./components/*.
 */
export default function Dashboard(): React.ReactElement {
  const navigate = useNavigate();
  const { data: dash, isLoading: dashLoading } = useDashboardData();
  const { data: queue } = useVelaQueue();

  const velaItems = queue ?? [];
  const velaCount = velaItems.length;

  const go = (href: string) => {
    if (href.startsWith('#') || !href) return;
    navigate(href);
  };

  // Loading state covers first paint only; after that, EMPTY_PAYLOAD renders fine
  if (dashLoading && !dash) {
    return (
      <div style={{ color: C.sub, fontSize: 13, padding: '40px 0', fontFamily: F.family }}>
        Loading your dashboard...
      </div>
    );
  }

  const d = dash!;

  const todayCashBalance = d.bankAccounts.reduce((s, a) => s + a.bankBalance, 0);
  const totalBankBalance = todayCashBalance;

  const suggestions = DEFAULT_SUGGESTIONS; // swap for API-driven list later

  return (
    <div style={{ fontFamily: F.family }}>
      <GreetingBar
        onCustomize={() => navigate('/settings')}
        onPrivacy={() => navigate('/settings')}
      />

<ServicePills onServiceClick={(svc) => go(svc.href)} />

      <CreateActions
        onActionClick={(a) => go(a.href)}
        onShowAll={() => navigate('/invoices/all')}
      />

            <VelaPreparedHero
        count={velaCount}
        onReviewAll={() => navigate('/agents?filter=queue')}
      />

      <BusinessGlance
        profitLoss={d.profitLoss}
        expenseCategories={d.expenseCategories}
        expensesTotal={d.stats.expensesThisMonth}
        expensesDeltaPct={0}
        bankAccounts={d.bankAccounts}
        totalBankBalance={totalBankBalance}
        onViewPL={() => navigate('/reports')}
        onViewExpenses={() => navigate('/billpay')}
        onGoToRegisters={() => navigate('/payroll/bank-accounts')}
      />

      <div style={{
        display: 'grid',
        gridTemplateColumns: '1.1fr 1fr',
        gap: 18,
        marginBottom: 24,
      }}>
        <VelaReviewQueue
          items={velaItems}
          totalCount={velaCount}
          onReviewItem={(item) => go(item.href)}
          onReviewAll={() => navigate('/agents?filter=queue')}
        />
        <CashFlowCard
          points={d.cashFlow}
          todayCashBalance={todayCashBalance}
          onViewDetail={() => navigate('/reports/planning/cash-flow-overview')}
        />
      </div>

      <div style={{
        display: 'grid',
        gridTemplateColumns: '1.3fr 1fr',
        gap: 18,
      }}>
        <RecentTransactions
          transactions={d.recentTransactions}
          onViewAll={() => navigate('/transactions')}
          onReviewTransaction={(t) => navigate('/transactions?review=' + t.id)}
        />
        <Suggestions
          suggestions={suggestions}
          onSuggestionClick={(s) => go(s.href)}
        />
      </div>

      <AddWidgetsCard />

      <VelaChatButton inline onClick={() => navigate('/agents')} />
    </div>
  );
}
